/**
 * Client Diagnostics Ring
 *
 * A bounded, module-scope ring of the most recent client-side failures. It
 * exists so a product report can cite a REAL failed request — path, status and
 * the response's `X-Request-Id` — instead of a member's recollection of "it
 * broke". `useArtemisClientState` reads the ring into
 * `ephemeral.diagnostics.entries`; `bin/worktree-observe requests` then finds
 * the server-side line whose `request_id` matches.
 *
 * WHO PUSHES. `services/apiClient.js` is the ONE api_error / network_error
 * writer: every v2 call goes through `baseFetch`, so wiring it there covers the
 * whole surface rather than per call site. The global error handlers
 * (`app.config.errorHandler`, `window.onerror`, `unhandledrejection`) push
 * `uncaught` / `unhandled_rejection` through `pushErrorDiagnostic`.
 *
 * WHY THE RULES LIVE HERE, NOT AT THE CALL SITES. Truncation, path
 * normalization, kind validation, top-frame extraction and the request-id
 * header name are all rules about what a diagnostic IS. Keeping them in this
 * module means every writer records the same shape, and the envelope's size
 * bound is provable from one file instead of audited across four.
 *
 * THE MODULE MUST STAY A LEAF. It imports nothing — no store, no composable,
 * no service. `apiClient` imports it, and `apiClient` is the foundation every
 * store's data path already sits on; an import in the other direction would
 * make the diagnostics ring part of the store-init cycle it is supposed to
 * observe from outside.
 *
 * NEVER THROW INTO THE CALLER. This is a contract, not defensive style: a
 * diagnostics ring that can break a request is strictly worse than no ring at
 * all. Every writer swallows its own failures, and `readDiagnostics` answers
 * an EMPTY ARRAY — never `undefined` — when nothing has failed, so the
 * envelope builder has no absent-vs-empty case to reason about.
 */

/** The four failure classes a report can cite. Anything else is dropped. */
export const DIAGNOSTIC_KINDS = Object.freeze([
  'api_error',
  'network_error',
  'uncaught',
  'unhandled_rejection',
])

/**
 * Ring bound and per-field caps.
 *
 * The bound is what makes bead 2.4's acceptance criterion hold: the client
 * state envelope must stay under `CLIENT_STATE_SIZE_WARN_BYTES` (32 KB, in
 * `api/v2/artemis_controller.rb`). Worst case here is 20 entries x (200-char
 * message + four 120-char fields + a 24-char timestamp + JSON overhead) —
 * roughly 16 KB, and a realistic entry is ~430 bytes, so ~8.6 KB for a full
 * ring. Both leave room for the rest of the envelope.
 */
export const MAX_DIAGNOSTIC_ENTRIES = 20
export const MAX_DIAGNOSTIC_MESSAGE_CHARS = 200
export const MAX_DIAGNOSTIC_FIELD_CHARS = 120

/** The header Rails' `ActionDispatch::RequestId` middleware sets on every response. */
export const REQUEST_ID_HEADER = 'X-Request-Id'

// Oldest first, newest last — the order `readDiagnostics` promises and the
// order a reader scans to find "what happened just before I hit report".
let entries = []

/**
 * Cap a string field, or answer null for anything that is not a usable string.
 * Non-strings become null rather than `String(value)` noise: a diagnostic
 * field whose value is "[object Object]" is worse than an absent one, because
 * the evaluator would treat it as signal.
 */
const capString = (value, max) => {
  if (typeof value !== 'string') return null
  const trimmed = value.trim()
  if (trimmed.length === 0) return null
  return trimmed.slice(0, max)
}

/**
 * A path, not a URL: everything before the first `?` or `#`. A query string
 * carries viewport bounds and filter criteria — bulk that would evict real
 * entries from a 20-slot ring, and the criteria already ride the envelope's
 * own filter state.
 */
const normalizePath = (value) => {
  const raw = capString(value, MAX_DIAGNOSTIC_FIELD_CHARS + 200)
  if (raw === null) return null
  return capString(raw.split(/[?#]/)[0], MAX_DIAGNOSTIC_FIELD_CHARS)
}

/**
 * The FIRST stack frame and nothing else. A full stack is kilobytes of
 * bundler-mangled paths; the top frame is the one line that says where the
 * failure happened. Matches both shapes a browser produces:
 * V8 `    at foo (http://host/x.js:1:2)` and
 * SpiderMonkey/JavaScriptCore `foo@http://host/x.js:1:2`.
 */
const topFrameOf = (stack) => {
  if (typeof stack !== 'string') return null
  for (const line of stack.split('\n')) {
    const trimmed = line.trim()
    if (trimmed.startsWith('at ') || /@.+:\d+/.test(trimmed)) {
      return trimmed
    }
  }
  return null
}

/**
 * Record one failure. Silently drops an entry whose `kind` is not one of the
 * four — a ring carrying a kind no reader understands is worse than a shorter
 * ring — and silently swallows any failure of its own.
 *
 * @param {Object} entry
 * @param {string} entry.kind - One of `DIAGNOSTIC_KINDS`.
 * @param {string} [entry.path] - The version-relative endpoint, query stripped.
 * @param {string} [entry.method] - HTTP method.
 * @param {number} [entry.status] - HTTP status, when a response was received.
 * @param {string} [entry.code] - The server's structured error code.
 * @param {string} [entry.request_id] - The response's `X-Request-Id`.
 * @param {string} [entry.message] - Human-readable detail, capped at 200 chars.
 * @returns {void}
 */
export const pushDiagnostic = (entry) => {
  try {
    if (!entry || !DIAGNOSTIC_KINDS.includes(entry.kind)) return

    // Frozen so a reader cannot mutate what a later reader will see, and
    // fully keyed so the serialized shape never varies between entries.
    const record = Object.freeze({
      at: new Date().toISOString(),
      kind: entry.kind,
      path: normalizePath(entry.path),
      method: typeof entry.method === 'string'
        ? capString(entry.method.toUpperCase(), MAX_DIAGNOSTIC_FIELD_CHARS)
        : null,
      status: Number.isFinite(entry.status) ? entry.status : null,
      code: capString(entry.code, MAX_DIAGNOSTIC_FIELD_CHARS),
      request_id: capString(entry.request_id, MAX_DIAGNOSTIC_FIELD_CHARS),
      message: capString(entry.message, MAX_DIAGNOSTIC_MESSAGE_CHARS),
    })

    // Evict oldest first. `shift` mutates the live array only; an array
    // already handed out by `readDiagnostics` is a separate copy.
    while (entries.length >= MAX_DIAGNOSTIC_ENTRIES) entries.shift()
    entries.push(record)
  } catch {
    // The contract: a diagnostics failure is never the caller's problem.
  }
}

/**
 * Record a thrown value as `uncaught` or `unhandled_rejection`, keeping its
 * message and top frame only. Accepts an Error, a bare string
 * (`window.onerror`'s first argument), or any rejected value — an
 * `unhandledrejection` reason is frequently not an Error at all.
 *
 * @param {string} kind - `uncaught` or `unhandled_rejection`.
 * @param {*} thrown - The Error, message, or rejected value.
 * @returns {void}
 */
export const pushErrorDiagnostic = (kind, thrown) => {
  try {
    let message = null

    if (typeof thrown === 'string') {
      message = thrown
    } else if (thrown && typeof thrown === 'object') {
      const base = typeof thrown.message === 'string' && thrown.message.length > 0
        ? thrown.message
        : null
      const frame = topFrameOf(thrown.stack)
      message = base && frame ? `${base} @ ${frame}` : (base || frame)
    } else if (thrown !== null && thrown !== undefined) {
      message = String(thrown)
    }

    pushDiagnostic({ kind, message })
  } catch {
    // Same contract: a global error handler must not fail while reporting.
  }
}

/**
 * Read the response's request id, or null. Separate from the push so the
 * header read cannot throw inside a CALLER'S try block: `pushDiagnostic`'s own
 * try/catch cannot protect its arguments, which are evaluated first, and a
 * `response` without a `headers` object would otherwise raise a TypeError
 * inside `baseFetch` — misclassifying an HTTP error as a network failure and
 * skipping the step-up handshake.
 *
 * @param {Response} response
 * @returns {string|null}
 */
export const readRequestId = (response) => {
  try {
    return response?.headers?.get?.(REQUEST_ID_HEADER) ?? null
  } catch {
    return null
  }
}

/**
 * The ring, oldest first, as a copy. A copy because the envelope builder
 * serializes what it reads, and handing out the live array would let a push
 * landing mid-flight mutate an envelope already under construction.
 *
 * @returns {Array<Object>} Empty when nothing has failed — never `undefined`.
 */
export const readDiagnostics = () => entries.slice()

/**
 * Empty the ring. A test seam: module-scope state outlives a single `it`, and
 * an entry left by a neighbouring case would make an eviction or ordering
 * assertion depend on test order.
 *
 * @returns {void}
 */
export const clearDiagnostics = () => {
  entries = []
}
